import { fetchJSON as fetchAPI } from './client';

export type DailyOpsDashboard = {
  tenant_id: string;
  overall_executive_score: number;
  critical_priorities: number;
  active_risks: number;
  active_opportunities: number;
  today_revenue_forecast: number;
  today_profit_forecast: number;
  active_recommendations: number;
  latest_briefing_type?: string;
  modules_consolidated: number;
  generated_at?: string;
};

export type BriefingRecord = {
  id: string;
  type: string;
  title: string;
  summary?: string;
  highlights?: string[];
  key_metrics?: string[];
  top_priorities?: string[];
  recipients?: string[];
  channels?: string[];
  generated_at?: string;
};

export type PriorityRecord = {
  id: string;
  title: string;
  description?: string;
  category: string;
  expected_impact?: string;
  urgency_score: number;
  confidence_score: number;
  priority_rank: number;
  source_module?: string;
  requires_approval: boolean;
  created_at?: string;
};

export type ForecastRecord = {
  id: string;
  revenue_forecast: number;
  profit_forecast: number;
  inventory_consumption_units: number;
  advertising_spend: number;
  cash_flow_forecast: number;
  operational_load?: string;
  potential_risks?: string[];
  potential_opportunities?: string[];
  generated_at?: string;
};

export type HealthScoreRecord = {
  id: string;
  business_health: number;
  growth_health: number;
  profit_health: number;
  inventory_health: number;
  advertising_health: number;
  content_health: number;
  brand_health: number;
  customer_health: number;
  marketplace_health: number;
  overall_executive_score: number;
  generated_at?: string;
};

export type OpsRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  business_context?: string;
  expected_impact?: string;
  confidence_score: number;
  dependencies?: string[];
  next_steps?: string[];
  module_links?: string[];
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
  created_at?: string;
};

export type RiskRecord = {
  id: string;
  title: string;
  description?: string;
  category?: string;
  severity?: string;
  impact?: string;
  source_module?: string;
  created_at?: string;
};

export type OpportunityRecord = {
  id: string;
  title: string;
  description?: string;
  category?: string;
  expected_roi: number;
  confidence_score: number;
  source_module?: string;
  created_at?: string;
};

export type GenerateBriefingRequest = {
  type?: string;
  recipients?: string[];
  channels?: string[];
};

const API_BASE = import.meta.env.VITE_DAILYOPS_API ?? '/api/v1/dailyops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const dailyopsApi = {
  dashboard: () => fetchJSON<DailyOpsDashboard>('/dashboard'),
  listBriefings: () => fetchJSON<BriefingRecord[]>('/briefings'),
  generateBriefing: (body: GenerateBriefingRequest = { type: 'morning' }) =>
    fetchJSON<BriefingRecord>('/briefings/generate', { method: 'POST', body: JSON.stringify(body) }),
  listPriorities: () => fetchJSON<PriorityRecord[]>('/priorities'),
  listForecasts: () => fetchJSON<ForecastRecord[]>('/forecasts'),
  listHealthScores: () => fetchJSON<HealthScoreRecord[]>('/health-scores'),
  listRecommendations: () => fetchJSON<OpsRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<OpsRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listRisks: () => fetchJSON<RiskRecord[]>('/risks'),
  listOpportunities: () => fetchJSON<OpportunityRecord[]>('/opportunities'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
